import { useCallback } from 'react';
import {
  closeNoteModal,
  openNoteModal,
  type NoteModalConfig
} from 'store/noteModal/noteModalSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import type { Client, StatusesItem } from 'store/clients/clientsSlice';

import NoteModal from './noteModal';

type StatusSelectionNoteData = {
  statusInfo?: {
    status?: {
      id?: StatusesItem['id'];
    };
  };
  client?: Partial<Client> & {
    id?: string | number;
    name?: string;
    prependedId?: string | number;
  };
};

export const getStatusSelectionNoteModalConfig = (
  data: StatusSelectionNoteData | undefined,
  statusesList: StatusesItem[] | undefined
): NoteModalConfig | null => {
  const selectedStatusId = data?.statusInfo?.status?.id;
  const client = data?.client;
  const noteConfig = statusesList?.find((status) => status.id === selectedStatusId)?.noteConfig;

  if (
    !selectedStatusId ||
    !client?.id ||
    !noteConfig?.noteTriggeredOnStatusSelection ||
    selectedStatusId === client.status?.id
  ) {
    return null;
  }

  const clientName = client.name ?? [client.firstName, client.lastName].filter(Boolean).join(' ');

  return {
    status: selectedStatusId,
    clientId: client.id.toString(),
    clientName,
    prependedClientId: client.prependedId?.toString() ?? '',
    note: {
      noteType: noteConfig.preselectedNoteType?.id ?? ''
    }
  };
};

export const useNoteModal = () => {
  const dispatch = useAppDispatch();
  const { config, hidden } = useAppSelector((state) => state.noteModal);

  const handleOpen = useCallback(
    (payload: NoteModalConfig) => {
      dispatch(openNoteModal(payload));
    },
    [dispatch]
  );

  const handleClose = useCallback(() => {
    dispatch(closeNoteModal());
  }, [dispatch]);

  const open = config !== null;

  const noteModalElement = config ? (
    <NoteModal
      status={config.status}
      open={open}
      hidden={hidden}
      clientId={config.clientId}
      clientName={config.clientName}
      prependedClientId={config.prependedClientId}
      handleClose={handleClose}
      note={config.note ?? undefined}
      onSubmitSuccess={handleClose}
      onDeleteSuccess={handleClose}
    />
  ) : null;

  return {
    config,
    hidden,
    open,
    handleOpen,
    handleClose,
    note: config?.note ?? null,
    noteModalElement
  };
};
